import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface NotificationContextType {
  getUnseenCount: (moduleId: string) => number;
  markAsSeen: (moduleId: string) => void;
  updateCount: (moduleId: string, count: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  // Store current count and last seen count for each module
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [seenCounts, setSeenCounts] = useState<Record<string, number>>({});

  const updateCount = useCallback((moduleId: string, count: number) => {
    setCounts((prev) => {
      // Only update if count increased (new entries)
      if (prev[moduleId] === undefined || count > prev[moduleId]) {
        return { ...prev, [moduleId]: count };
      }
      return prev;
    });
  }, []);

  const markAsSeen = useCallback((moduleId: string) => {
    setSeenCounts((prev) => ({
      ...prev,
      [moduleId]: counts[moduleId] || 0,
    }));
  }, [counts]);

  const getUnseenCount = useCallback(
    (moduleId: string) => {
      const currentCount = counts[moduleId] || 0;
      const lastSeenCount = seenCounts[moduleId] || 0;
      return Math.max(0, currentCount - lastSeenCount);
    },
    [counts, seenCounts],
  );

  return (
    <NotificationContext.Provider value={{ getUnseenCount, markAsSeen, updateCount }}>
      {children}
    </NotificationContext.Provider>
  );
};

